<template>
  <Header />

  <!-- Enhanced Hero Section -->
  <section class="relative bg-gradient-to-br from-red-50 via-white to-orange-50 py-4 lg:py-8">
    <div class="max-w-5xl mx-auto px-6 text-center">
      <div class="mb-12">
        <div class="inline-flex items-center bg-red-100 text-red-800 px-4 py-2 rounded-full text-sm font-medium mb-6">
          🎁 Join 1,200+ thoughtful gift givers
        </div>
        <h1 class="text-4xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight">
          Never forget the <span
            class="text-transparent bg-clip-text bg-gradient-to-r from-red-600 to-orange-600"
>perfect gift</span> again
        </h1>
        <p class="text-xl text-gray-600 mb-8 max-w-2xl mx-auto leading-relaxed">
          Make every occasion memorable with thoughtful, planned gifts.
        </p>
      </div>

      <!-- Enhanced CTA Form -->
      <div class="max-w-md mx-auto mb-8">
        <div class="bg-white rounded-2xl shadow-lg border border-gray-200 p-6">
          <!-- Google Sign-in Button -->
          <div class="mb-4">
            <GoogleSignInButton @error="handleGoogleError" />
          </div>

          <!-- Divider -->
          <div class="relative mb-4">
            <div class="absolute inset-0 flex items-center">
              <div class="w-full border-t border-gray-300"></div>
            </div>
            <div class="relative flex justify-center text-sm">
              <span class="px-2 bg-white text-gray-500">Or start with email</span>
            </div>
          </div>

          <!-- Email Form -->
          <div class="flex">
            <div class="flex-1 relative">
              <input
type="email" v-model="registerEmail"
                class="w-full px-4 py-4 text-gray-900 placeholder-gray-500 border-0 rounded-xl focus:ring-2 focus:ring-blue-500 focus:outline-none"
                placeholder="Enter your email to get started..."
>
            </div>
            <button
@click="redirectToRegister()"
              class="px-6 py-4 bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-700 hover:to-orange-700 text-white font-semibold rounded-xl transition-all duration-200 hover:shadow-lg transform hover:scale-105 ml-2"
>
              Get Started
            </button>
          </div>

          <!-- Google Error Message -->
          <div v-if="googleErrorMessage" class="mt-4 bg-red-50 border border-red-200 rounded-lg p-3">
            <p class="text-red-800 text-sm">
              <span class="font-medium">Google Sign-in Error:</span> {{ googleErrorMessage }}
            </p>
          </div>
        </div>
        <p class="text-sm text-gray-500 mt-3">Free to get started • No credit card required</p>
      </div>

      <!-- Quick Stats -->
      <div class="grid grid-cols-3 gap-8 max-w-lg mx-auto">
        <div class="text-center">
          <div class="text-2xl font-bold text-gray-900">1,200+</div>
          <div class="text-sm text-gray-600">Active Users</div>
        </div>
        <div class="text-center">
          <div class="text-2xl font-bold text-gray-900">10K+</div>
          <div class="text-sm text-gray-600">Gifts Saved</div>
        </div>
        <div class="text-center">
          <div class="text-2xl font-bold text-gray-900">99%</div>
          <div class="text-sm text-gray-600">Never Forget</div>
        </div>
      </div>
    </div>
  </section>

  <!-- Features Section -->
  <section class="py-20 bg-white">
    <div class="max-w-6xl mx-auto px-6">
      <div class="text-center mb-16">
        <h2 class="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
          Everything you need for perfect gift planning
        </h2>
        <p class="text-xl text-gray-600 max-w-2xl mx-auto">
          Our platform makes gift-giving effortless with powerful features designed for thoughtful people.
        </p>
      </div>

      <div class="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
        <!-- Feature 1: Save Gift Ideas -->
        <div
          class="bg-white rounded-xl border border-gray-200 p-8 hover:shadow-lg transition-all duration-200 hover:border-blue-200"
>
          <div
            class="w-12 h-12 bg-gradient-to-br from-blue-100 to-blue-200 rounded-xl flex items-center justify-center mb-6"
>
            <svg
class="w-6 h-6 text-blue-600 lucide lucide-gift-icon lucide-gift" xmlns="http://www.w3.org/2000/svg"
              width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
              stroke-linecap="round" stroke-linejoin="round"
>
              <rect x="3" y="8" width="18" height="4" rx="1" />
              <path d="M12 8v13" />
              <path d="M19 12v7a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2v-7" />
              <path d="M7.5 8a2.5 2.5 0 0 1 0-5A4.8 8 0 0 1 12 8a4.8 8 0 0 1 4.5-5 2.5 2.5 0 0 1 0 5" />
            </svg>
          </div>
          <h3 class="text-xl font-semibold text-gray-900 mb-3">Save Gift Ideas</h3>
          <p class="text-gray-600 leading-relaxed">
            Capture gift ideas the moment you think of them. Never lose that perfect gift idea again.
          </p>
        </div>

        <!-- Feature 2: Track People -->
        <div
          class="bg-white rounded-xl border border-gray-200 p-8 hover:shadow-lg transition-all duration-200 hover:border-purple-200"
>
          <div
            class="w-12 h-12 bg-gradient-to-br from-purple-100 to-purple-200 rounded-xl flex items-center justify-center mb-6"
>
            <svg
class="w-6 h-6 text-purple-600 lucide lucide-users-round-icon lucide-users-round"
              xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
>
              <path d="M18 21a8 8 0 0 0-16 0" />
              <circle cx="10" cy="8" r="5" />
              <path d="M22 20c0-3.37-2-6.5-4-8a5 5 0 0 0-.45-8.3" />
            </svg>
          </div>
          <h3 class="text-xl font-semibold text-gray-900 mb-3">Track Loved Ones</h3>
          <p class="text-gray-600 leading-relaxed">
            Keep track of friends and family with their birthdays, anniversaries, and preferences.
          </p>
        </div>

        <!-- Feature 3: Smart Reminders -->
        <div
          class="bg-white rounded-xl border border-gray-200 p-8 hover:shadow-lg transition-all duration-200 hover:border-green-200"
>
          <div
            class="w-12 h-12 bg-gradient-to-br from-green-100 to-green-200 rounded-xl flex items-center justify-center mb-6"
>
            <svg
class="w-6 h-6 text-green-600 lucide lucide-bell-ring-icon lucide-bell-ring"
              xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
>
              <path d="M10.268 21a2 2 0 0 0 3.464 0" />
              <path d="M22 8c0-2.3-.8-4.3-2-6" />
              <path
                d="M3.262 15.326A1 1 0 0 0 4 17h16a1 1 0 0 0 .74-1.673C19.41 13.956 18 12.499 18 8A6 6 0 0 0 6 8c0 4.499-1.411 5.956-2.738 7.326"
/>
              <path d="M4 2C2.8 3.7 2 5.7 2 8" />
            </svg>
          </div>
          <h3 class="text-xl font-semibold text-gray-900 mb-3">Smart Reminders</h3>
          <p class="text-gray-600 leading-relaxed">
            Get timely email reminders before birthdays, holidays, and special occasions.
          </p>
        </div>

        <!-- Feature 4: Stay Organized -->
        <div
          class="bg-white rounded-xl border border-gray-200 p-8 hover:shadow-lg transition-all duration-200 hover:border-orange-200"
>
          <div
            class="w-12 h-12 bg-gradient-to-br from-orange-100 to-orange-200 rounded-xl flex items-center justify-center mb-6"
>
            <svg
class="w-6 h-6 text-orange-600 lucide lucide-notebook-pen-icon lucide-notebook-pen"
              xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
>
              <path d="M13.4 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-7.4" />
              <path d="M2 6h4" />
              <path d="M2 10h4" />
              <path d="M2 14h4" />
              <path d="M2 18h4" />
              <path
                d="M21.378 5.626a1 1 0 1 0-3.004-3.004l-5.01 5.012a2 2 0 0 0-.506.854l-.837 2.87a.5.5 0 0 0 .62.62l2.87-.837a2 2 0 0 0 .854-.506z"
/>
            </svg>
          </div>
          <h3 class="text-xl font-semibold text-gray-900 mb-3">Stay Organized</h3>
          <p class="text-gray-600 leading-relaxed">
            Match gifts to people, track what you've purchased, and plan ahead with confidence.
          </p>
        </div>
      </div>
    </div>
  </section>

  <!-- How It Works Section -->
  <section class="py-20 bg-gradient-to-br from-red-50 via-white to-orange-50">
    <div class="max-w-4xl mx-auto px-6">
      <div class="text-center mb-16">
        <h2 class="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
          How Present Planner Works
        </h2>
        <p class="text-xl text-gray-600">Simple steps to transform your gift-giving experience</p>
      </div>

      <div class="grid md:grid-cols-3 gap-12">
        <!-- Step 1 -->
        <div class="text-center">
          <div class="relative mb-8">
            <div
              class="w-20 h-20 bg-gradient-to-br from-green-500 to-green-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg"
>
              <span class="text-2xl font-bold text-white">1</span>
            </div>
            <img
src="@/assets/gifts.png" class="w-full rounded-xl shadow-lg border border-gray-200"
              alt="Save gift ideas"
>
          </div>
          <h3 class="text-xl font-semibold text-gray-900 mb-3">Save gift ideas as you find them</h3>
          <p class="text-gray-600">
            Capture gift ideas as you discover them throughout the year. Add photos, descriptions, and prices.
          </p>
        </div>

        <!-- Step 2 -->
        <div class="text-center">
          <div class="relative mb-8">
            <div
              class="w-20 h-20 bg-gradient-to-br white rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg"
>
              <span class="text-2xl font-bold text-black">2</span>
            </div>
            <img
src="@/assets/friends.png" class="w-full rounded-xl shadow-lg border border-gray-200"
              alt="Add friends and family"
>
          </div>
          <h3 class="text-xl font-semibold text-gray-900 mb-3">Add your friends and family</h3>
          <p class="text-gray-600">
            Add friends and family with their important dates. Match your saved gifts to the right people.
          </p>
        </div>

        <!-- Step 3 -->
        <div class="text-center">
          <div class="relative mb-8">
            <div
              class="w-20 h-20 bg-gradient-to-br from-red-500 to-red-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg"
>
              <span class="text-2xl font-bold text-white">3</span>
            </div>
            <img
src="@/assets/upcoming.png" class="w-full rounded-xl shadow-lg border border-gray-200"
              alt="Get reminders"
>
          </div>
          <h3 class="text-xl font-semibold text-gray-900 mb-3">Get reminded before special dates</h3>
          <p class="text-gray-600">
            Get email reminders 2 weeks before, the day before, and the day of, birthdays, holidays, and special
            occasions.
          </p>
        </div>
      </div>
    </div>
  </section>


  <!-- Pricing Section -->
  <section class="py-20 bg-white">
    <div class="max-w-4xl mx-auto px-6">
      <div class="text-center mb-16">
        <h2 class="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
          Simple, Transparent Pricing
        </h2>
        <p class="text-xl text-gray-600 max-w-2xl mx-auto">
          Start free and upgrade when you're ready. No subscriptions, no recurring fees.
        </p>
      </div>

      <div class="grid md:grid-cols-2 gap-8 max-w-3xl mx-auto">
        <!-- Free Plan -->
        <div class="bg-white rounded-xl border-2 border-gray-200 p-8 hover:shadow-lg transition-all duration-200">
          <div class="text-center mb-6">
            <h3 class="text-2xl font-bold text-gray-900 mb-2">Free</h3>
            <div class="text-4xl font-bold text-gray-900 mb-1">$0</div>
            <p class="text-gray-600">Forever free</p>
          </div>

          <ul class="space-y-4 mb-8">
            <li class="flex items-center">
              <svg class="w-5 h-5 text-green-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
              </svg>
              <span class="text-gray-700">Up to 10 gift ideas</span>
            </li>
            <li class="flex items-center">
              <svg class="w-5 h-5 text-green-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
              </svg>
              <span class="text-gray-700">Save gifts for up to 5 people</span>
            </li>
          </ul>

          <button
@click="redirectToRegister()"
            class="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold py-3 px-6 rounded-lg transition-colors duration-200"
>
            Get Started Free
          </button>
        </div>

        <!-- Premium Plan -->
        <div
          class="bg-gradient-to-br from-red-50 to-orange-50 rounded-xl border-2 border-red-200 p-8 hover:shadow-xl transition-all duration-200 relative"
>
          <!-- Popular Badge -->
          <div class="text-center mb-6">
            <h3 class="text-2xl font-bold text-gray-900 mb-2">Premium</h3>
            <div class="text-4xl font-bold text-gray-900 mb-1">
$10 <span
                class="text-lg font-normal text-gray-600"
>AUD</span>
</div>
            <p class="text-gray-600"><u>One-time payment</u>, lifetime access</p>
          </div>

          <ul class="space-y-4 mb-8">
            <li class="flex items-center">
              <svg class="w-5 h-5 text-green-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
              </svg>
              <span class="text-gray-700"><strong>Unlimited</strong> gift ideas</span>
            </li>
            <li class="flex items-center">
              <svg class="w-5 h-5 text-green-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
              </svg>
              <span class="text-gray-700"><strong>Unlimited</strong> people</span>
            </li>
          </ul>

          <button
@click="redirectToRegister()"
            class="w-full bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-700 hover:to-orange-700 text-white font-semibold py-3 px-6 rounded-lg transition-all duration-200 hover:shadow-lg transform hover:scale-105"
>
            Start Free, Upgrade Later
          </button>
        </div>
      </div>
    </div>
  </section>


  <!-- Enhanced Testimonials Section -->
  <section class="py-20 bg-gradient-to-br from-red-50 via-white to-orange-50">
    <div class="max-w-6xl mx-auto px-6">
      <div class="text-center mb-16">
        <h2 class="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
          What Our Users Say
        </h2>
        <p class="text-xl text-gray-600">Join thousands of people who have transformed their gift-giving</p>
      </div>

      <div class="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
        <!-- Testimonial 1 -->
        <div class="bg-white rounded-xl border border-gray-200 p-8 hover:shadow-lg transition-all duration-200">
          <div class="flex items-center mb-6">
            <div
              class="w-12 h-12 bg-gradient-to-br from-blue-100 to-blue-200 rounded-full flex items-center justify-center mr-4"
>
              <span class="text-blue-600 font-bold text-lg">MR</span>
            </div>
            <div>
              <h4 class="font-semibold text-gray-900">Maya R.</h4>
              <div class="flex text-yellow-400 text-sm">★★★★★</div>
            </div>
          </div>
          <p class="text-gray-600 italic leading-relaxed">
            "As someone with ADHD, I used to panic every birthday and holiday. Present Planner has been a game-changer -
            I can capture gift ideas when I see them and never lose track again!"
          </p>
        </div>

        <!-- Testimonial 2 -->
        <div class="bg-white rounded-xl border border-gray-200 p-8 hover:shadow-lg transition-all duration-200">
          <div class="flex items-center mb-6">
            <div
              class="w-12 h-12 bg-gradient-to-br from-purple-100 to-purple-200 rounded-full flex items-center justify-center mr-4"
>
              <span class="text-purple-600 font-bold text-lg">DL</span>
            </div>
            <div>
              <h4 class="font-semibold text-gray-900">David L.</h4>
              <div class="flex text-yellow-400 text-sm">★★★★★</div>
            </div>
          </div>
          <p class="text-gray-600 italic leading-relaxed">
            "Finally, a gift planning tool that works with my brain! The reminders mean I'm never caught off-guard, and
            my family thinks I've become incredibly thoughtful."
          </p>
        </div>

        <!-- Testimonial 3 -->
        <div class="bg-white rounded-xl border border-gray-200 p-8 hover:shadow-lg transition-all duration-200">
          <div class="flex items-center mb-6">
            <div
              class="w-12 h-12 bg-gradient-to-br from-green-100 to-green-200 rounded-full flex items-center justify-center mr-4"
>
              <span class="text-green-600 font-bold text-lg">AL</span>
            </div>
            <div>
              <h4 class="font-semibold text-gray-900">Alex L.</h4>
              <div class="flex text-yellow-400 text-sm">★★★★★</div>
            </div>
          </div>
          <p class="text-gray-600 italic leading-relaxed">
            "Being neurodivergent, staying organised is challenging. This app lets me save gift ideas the moment I think
            of them, and the email reminders are lifesavers"
          </p>
        </div>

        <!-- Testimonial 4 -->
        <div class="bg-white rounded-xl border border-gray-200 p-8 hover:shadow-lg transition-all duration-200">
          <div class="flex items-center mb-6">
            <div
              class="w-12 h-12 bg-gradient-to-br from-orange-100 to-orange-200 rounded-full flex items-center justify-center mr-4"
>
              <span class="text-orange-600 font-bold text-lg">JK</span>
            </div>
            <div>
              <h4 class="font-semibold text-gray-900">Jessica K.</h4>
              <div class="flex text-yellow-400 text-sm">★★★★★</div>
            </div>
          </div>
          <p class="text-gray-600 italic leading-relaxed">
            "I went from last-minute Amazon gift cards to being the person everyone asks for gift advice. Present
            Planner turned my chaotic gift-giving into something I'm proud of."
          </p>
        </div>

        <!-- Testimonial 5 -->
        <div class="bg-white rounded-xl border border-gray-200 p-8 hover:shadow-lg transition-all duration-200">
          <div class="flex items-center mb-6">
            <div
              class="w-12 h-12 bg-gradient-to-br from-pink-100 to-pink-200 rounded-full flex items-center justify-center mr-4"
>
              <span class="text-pink-600 font-bold text-lg">ST</span>
            </div>
            <div>
              <h4 class="font-semibold text-gray-900">Sam T.</h4>
              <div class="flex text-yellow-400 text-sm">★★★★★</div>
            </div>
          </div>
          <p class="text-gray-600 italic leading-relaxed">
            "I used to jot down gift ideas in my notes app and could never find them when I needed them. Present
            Planner's simplicity is genius - everything's organised, searchable, and actually useful!"
          </p>
        </div>

        <!-- Testimonial 6 -->
        <div class="bg-white rounded-xl border border-gray-200 p-8 hover:shadow-lg transition-all duration-200">
          <div class="flex items-center mb-6">
            <div
              class="w-12 h-12 bg-gradient-to-br from-indigo-100 to-indigo-200 rounded-full flex items-center justify-center mr-4"
>
              <span class="text-indigo-600 font-bold text-lg">CM</span>
            </div>
            <div>
              <h4 class="font-semibold text-gray-900">Casey M.</h4>
              <div class="flex text-yellow-400 text-sm">★★★★★</div>
            </div>
          </div>
          <p class="text-gray-600 italic leading-relaxed">
            "This has eliminated so much stress from my life. I can plan ahead, stay organized, and give gifts that
            truly mean something. My friends are always impressed!"
          </p>
        </div>
      </div>
    </div>
  </section>

  <!-- Final CTA Section -->
  <section class="py-20 bg-gradient-to-br from-red-600 to-orange-500">
    <div class="max-w-4xl mx-auto px-6 text-center">
      <h2 class="text-3xl md:text-4xl font-bold text-white mb-6">
        Ready to become a thoughtful gift giver?
      </h2>
      <p class="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
        Join thousands of people who never forget special occasions or run out of gift ideas.
      </p>

      <div class="max-w-md mx-auto mb-8">
        <div class="bg-white rounded-2xl shadow-xl p-2">
          <div class="flex">
            <input
type="email" v-model="registerEmail"
              class="flex-1 px-4 py-4 text-gray-900 placeholder-gray-500 border-0 rounded-xl focus:ring-2 focus:ring-blue-500 focus:outline-none"
              placeholder="Enter your email address..."
>
            <button
@click="redirectToRegister()"
              class="px-6 py-4 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-semibold rounded-xl transition-all duration-200 hover:shadow-lg transform hover:scale-105"
>
              Start Free
            </button>
          </div>
        </div>
      </div>

      <div class="flex items-center justify-center space-x-8 text-blue-100">
        <div class="flex items-center">
          <svg class="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
            <path
fill-rule="evenodd"
              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
              clip-rule="evenodd"
/>
          </svg>
          <span>Free tier</span>
        </div>
        <div class="flex items-center">
          <svg class="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
            <path
fill-rule="evenodd"
              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
              clip-rule="evenodd"
/>
          </svg>
          <span>Setup in 2 minutes</span>
        </div>
        <div class="flex items-center">
          <svg class="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
            <path
fill-rule="evenodd"
              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
              clip-rule="evenodd"
/>
          </svg>
          <span>Cancel anytime</span>
        </div>
      </div>
    </div>
  </section>

  <Footer />
</template>

<script>
import Header from "@/components/Header.vue";
import Footer from "@/components/Footer.vue";
import GoogleSignInButton from "@/components/GoogleSignInButton.vue";

export default {
  name: "HomeView",
  components: {
    Header,
    Footer,
    GoogleSignInButton,
  },
  data() {
    return {
      registerEmail: "",
      googleErrorMessage: ""
    };
  },
  created() { },
  computed: {
    isLoggedIn() {
      return this.$store.state.user.loggedIn;
    },
  },
  methods: {
    redirectToRegister() {
      if (this.registerEmail) {
        this.$router.push({ name: "Register", params: { email: this.registerEmail } });
      } else {
        this.$router.push({ name: "Register" });
      }
    },
    handleGoogleError(error) {
      this.googleErrorMessage = error;
      // Clear the error after 5 seconds
      setTimeout(() => {
        this.googleErrorMessage = "";
      }, 5000);
    }
  },
};
</script>
